import { useState, useCallback, useRef, useEffect } from "react";
import JobSearchClient, { ProspectiveCandidatesResponse, JobSearchResultCandidate } from "../../jobSearch/data/api/JobSearchClient";

export function useProspectiveCandidates(opportunityId: string | undefined, onCandidateAdded?: () => void) {
  const [data, setData] = useState<ProspectiveCandidatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep a ref to onCandidateAdded so handleAdd never captures a stale version
  const onCandidateAddedRef = useRef(onCandidateAdded);
  useEffect(() => {
    onCandidateAddedRef.current = onCandidateAdded;
  }, [onCandidateAdded]);

  const fetchData = useCallback(async () => {
    if (!opportunityId) return;
    try {
      const result = await JobSearchClient.getProspectiveCandidates(opportunityId);
      setData(result);
      setError(null);
      if (result.status === "PROCESSING") {
        pollingRef.current = setTimeout(fetchData, 3000);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load prospective candidates");
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [fetchData]);

  const handleAdd = useCallback(async (candidate: JobSearchResultCandidate) => {
    if (!candidate.candidate_id || addingId || !opportunityId) return;
    setAddingId(candidate.candidate_id);
    try {
      await JobSearchClient.addProspectiveCandidate(opportunityId, candidate.candidate_id);

      // Remove candidate from the prospective list immediately
      setData(prev => prev ? {
        ...prev,
        candidates: prev.candidates.filter(c => c.candidate_id !== candidate.candidate_id),
        total_results: (prev.total_results || 0) - 1,
      } : prev);

      // Refresh the applicants list — always calls the latest loadData via ref
      if (onCandidateAddedRef.current) {
        onCandidateAddedRef.current();
      }
    } catch (err: any) {
      setError(err.message || "Failed to add candidate");
    } finally {
      setAddingId(null);
    }
  }, [opportunityId, addingId]);

  return {
    data,
    loading,
    error,
    addingId,
    handleAdd,
    isProcessing: data?.status === "PROCESSING",
    candidates: data?.candidates || [],
    hasResults: (data?.candidates?.length || 0) > 0,
    setError
  };
}
